'use client';

import { useEffect, useRef } from 'react';

// Натуральный размер картинки карты — нужен для cover-маппинга.
const IMG_W = 1400;
const IMG_H = 740;

const COLORS = {
    dot: '#A0E641',
    dotGlow: '#A1E7434D',
    link: '#A0E641',
} as const;

// Улицы в координатах картинки (0..1) — из калибровки.
const PATHS: number[][][] = [
    [
        [0.001, 0.865],
        [0.999, 0.255],
    ],
    [
        [0.716, 0.0],
        [0.712, 0.736],
        [0.67, 0.812],
        [0.7, 0.88],
        [0.663, 0.995],
    ],
    [
        [0.404, 0.999],
        [0.944, 0.001],
    ],
];

const MOVING_COUNT = 21;
const STATIC_COUNT = 46;
const LINK_DISTANCE = 150; // базовая дистанция связей (image-px), масштабируется на cover

interface MovingDot {
    path: number;
    t: number;
    speed: number;
    rBase: number;
}

interface StaticDot {
    x: number;
    y: number;
    rBase: number;
    a: number;
}

interface Cover {
    s: number;
    ox: number;
    oy: number;
    dispW: number;
    dispH: number;
}

function pathLength(p: number[][]): number {
    let len = 0;
    for (let i = 1; i < p.length; i++) {
        len += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1]);
    }
    return len;
}

function pointAt(p: number[][], totalLen: number, t: number): [number, number] {
    let target = t * totalLen;
    for (let i = 1; i < p.length; i++) {
        const seg = Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1]);
        if (target <= seg) {
            const k = seg === 0 ? 0 : target / seg;
            return [p[i - 1][0] + (p[i][0] - p[i - 1][0]) * k, p[i - 1][1] + (p[i][1] - p[i - 1][1]) * k];
        }
        target -= seg;
    }
    const last = p[p.length - 1];
    return [last[0], last[1]];
}

const PATH_LEN = PATHS.map(pathLength);

export function useHeroMap() {
    const heroRef = useRef<HTMLElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const hero = heroRef.current;
        const canvas = canvasRef.current;
        if (!hero || !canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Инициализация точек (один раз на маунт).
        const moving: MovingDot[] = Array.from({ length: MOVING_COUNT }, (_, i) => ({
            path: i % PATHS.length,
            t: Math.random(),
            speed: (0.01 + Math.random() * 0.03) * (Math.random() < 0.5 ? -1 : 1),
            rBase: 2 + Math.random() * 7,
        }));

        const staticDots: StaticDot[] = Array.from({ length: STATIC_COUNT }, () => ({
            x: Math.random(),
            y: Math.random(),
            rBase: 1 + Math.random() * 5,
            a: 0.25 + Math.random() * 0.5,
        }));

        let W = 0;
        let H = 0;
        let dpr = 1;
        let cover: Cover = { s: 1, ox: 0, oy: 0, dispW: 0, dispH: 0 };
        let raf = 0;
        let last = performance.now();

        const computeCover = () => {
            // повторяет object-fit: cover; object-position: center
            const s = Math.max(W / IMG_W, H / IMG_H);
            const dispW = IMG_W * s;
            const dispH = IMG_H * s;
            cover = { s, ox: (W - dispW) / 2, oy: (H - dispH) / 2, dispW, dispH };
        };

        // нормированная точка картинки → координаты блока
        const mapImg = (nx: number, ny: number): [number, number] => [
            cover.ox + nx * cover.dispW,
            cover.oy + ny * cover.dispH,
        ];

        const resize = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            const r = hero.getBoundingClientRect();
            W = r.width;
            H = r.height;
            canvas.width = Math.round(W * dpr);
            canvas.height = Math.round(H * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            computeCover();
        };

        const drawDot = (x: number, y: number, r: number, alpha: number) => {
            ctx.beginPath();
            ctx.arc(x, y, r * 2.4, 0, Math.PI * 2);
            ctx.fillStyle = COLORS.dotGlow;
            ctx.globalAlpha = alpha * 0.5;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fillStyle = COLORS.dot;
            ctx.globalAlpha = alpha;
            ctx.fill();
            ctx.globalAlpha = 1;
        };

        const render = (now: number) => {
            const dt = Math.min((now - last) / 1000, 0.05);
            last = now;
            const s = cover.s;

            ctx.clearRect(0, 0, W, H);

            // статические точки (координаты блока)
            for (const st of staticDots) {
                drawDot(st.x * W, st.y * H, st.rBase * s, st.a);
            }

            // движущиеся точки (координаты картинки → cover-маппинг)
            const pts: { x: number; y: number; r: number }[] = [];
            for (const m of moving) {
                m.t += m.speed * dt;
                m.t = ((m.t % 1) + 1) % 1;
                const [nx, ny] = pointAt(PATHS[m.path], PATH_LEN[m.path], m.t);
                const [x, y] = mapImg(nx, ny);
                pts.push({ x, y, r: m.rBase * s });
            }

            // связи между близкими движущимися точками
            const max = LINK_DISTANCE * s;
            for (let i = 0; i < pts.length; i++) {
                for (let j = i + 1; j < pts.length; j++) {
                    const dx = pts[i].x - pts[j].x;
                    const dy = pts[i].y - pts[j].y;
                    const d2 = dx * dx + dy * dy;
                    if (d2 < max * max) {
                        const d = Math.sqrt(d2);
                        ctx.strokeStyle = COLORS.link;
                        ctx.globalAlpha = (1 - d / max) * 0.6;
                        ctx.lineWidth = 2;
                        ctx.beginPath();
                        ctx.moveTo(pts[i].x, pts[i].y);
                        ctx.lineTo(pts[j].x, pts[j].y);
                        ctx.stroke();
                        ctx.globalAlpha = 1;
                    }
                }
            }

            for (const p of pts) {
                drawDot(p.x, p.y, p.r, 1);
            }

            raf = requestAnimationFrame(render);
        };

        const ro = new ResizeObserver(resize);
        ro.observe(hero);

        resize();
        raf = requestAnimationFrame(render);

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, []);

    return { heroRef, canvasRef };
}
